import { BLOCKS, type Document } from '@contentful/rich-text-types'
import { decode } from 'html-entities'
import Head from 'next/head'
import { useContext, type ReactElement } from 'react'

import { useCommonServices } from '@lib/hooks'
import { type InjectedServerData } from '@lib/services/server-data'
import { withPublicCssUrl, withPublicImageUrl } from '@lib/utils/cdn'
import { getFontsForPreloadLinks, getStyleFontFaces, type TFontFamily } from '@lib/utils/fonts'
import { isHomepage } from '@lib/utils/page-rules/by-slug'
import { ensureLeadingSlash } from '@lib/utils/slugs'
import { composeUrl } from '@lib/utils/url'

import type { TypeSeoMetadataEntry } from '@contentful-types/seo-metadata'
import type { TypeSocialMetadataEntry } from '@contentful-types/social-metadata'

import { getConfig } from '@config/index'

import { CDN_URL } from '@constants/globals'

import { PageContext } from '@contexts/page-context'

import { updateAssetCDNUrl } from '@components/asset/utils'
import { renderRichTextToHtmlString } from '@components/renderer/rich-text'

import { getHeroImagePreloadProps } from './utils'

const baseUrl = getConfig().paths.basePath
const transcendConfigId = getConfig().thirdPartyConfig.transcend.id

const defaultTitle = 'Superhuman'

const faviconsOriginal: Array<{
  key: string
  rel: string
  type: string
  sizes?: string
  href: string
}> = [
  // Do NOT add a .ico file here - Chromium browsers will use it over SVG
  {
    key: 'svg',
    rel: 'icon',
    type: 'image/svg+xml',
    sizes: 'any',
    href: withPublicImageUrl('favicons/superhuman-icon.svg'),
  },
  {
    key: 'rastered-96x96',
    rel: 'icon',
    type: 'image/png',
    sizes: '96x96',
    href: withPublicImageUrl('favicons/superhuman-icon-96x96.png'),
  },
  {
    key: 'apple-touch-icon',
    rel: 'apple-touch-icon',
    type: 'image/png',
    sizes: '180x180',
    href: withPublicImageUrl('favicons/superhuman-apple-touch-icon.png'),
  },
]

type PageHeadProps = {
  seoMetadata?: TypeSeoMetadataEntry
  socialMetadata?: TypeSocialMetadataEntry
  heroImageSeed?: number | null
}

const socialOrSeo = (social: string, seo: string): string => {
  return social.trim() === '' ? seo : social
}

const renderRichTextToString = (richText: Document) => {
  return renderRichTextToHtmlString(richText, {
    [BLOCKS.PARAGRAPH]: (node, next) => {
      // decode html entity characters for Title and Description metadata fields
      return decode(next(node.content))
    },
  })
}

const serializeServerData = (obj: InjectedServerData) =>
  JSON.stringify(obj).replaceAll('<', String.raw`\u003c`)

export function PageHead({
  seoMetadata,
  socialMetadata,
  heroImageSeed,
}: PageHeadProps): ReactElement {
  const { slug, isPreview } = useContext(PageContext)

  const { shWebServerService } = useCommonServices()
  const data: InjectedServerData = shWebServerService.get()

  const fontFamilies: TFontFamily[] = ['Super Sans', 'Super Sans Mono', 'Super Serif']

  const {
    titleRichText,
    descriptionRichText,
    keywords = [],
    canonicalUrl = `${baseUrl}${ensureLeadingSlash(slug.trim())}`, // default to self-reference canonical url
    noindex,
  } = seoMetadata?.fields || {}
  const robots = [noindex ? 'noindex' : undefined].filter(x => x !== undefined)

  const seoTitle = titleRichText ? renderRichTextToString(titleRichText) : defaultTitle

  const seoDescription = descriptionRichText ? renderRichTextToString(descriptionRichText) : ''

  const {
    titleRichText: socialTitleRichText,
    descriptionRichText: socialDescriptionRichText,
    url: ogUrlString = '',
    type: ogType,
    image: ogImage,
  } = socialMetadata?.fields || {}

  const ogTitleString = socialTitleRichText ? renderRichTextToString(socialTitleRichText) : ''

  const ogDescriptionString = socialDescriptionRichText
    ? renderRichTextToString(socialDescriptionRichText)
    : ''

  const ogTitle = socialOrSeo(ogTitleString, seoTitle)
  const ogDescription = socialOrSeo(ogDescriptionString, seoDescription)
  const ogUrl = socialOrSeo(ogUrlString, canonicalUrl)

  const ogImageRelativeUrl = updateAssetCDNUrl(ogImage?.fields.asset?.fields.file?.url)
  const ogImageUrl =
    (ogImageRelativeUrl && composeUrl(ogImageRelativeUrl)) || withPublicImageUrl('social-share.png')

  const social = (
    <>
      {/**
       * Be careful: tags must be either direct children of the `Head` component or wrapped
       * in a maximum of one level of React.Fragment or arrays, otherwise they will not be
       * picked up on the client side.
       * https://nextjs.org/docs/api-reference/next/head
       */}
      {ogTitle.trim() && <meta property="og:title" content={ogTitle} />}
      <meta property="og:type" content={ogType} />
      {ogDescription.trim() && <meta property="og:description" content={ogDescription} />}
      {ogUrl.trim() && <meta property="og:url" content={ogUrl} />}
      {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
      {ogImageUrl && <meta name="twitter:card" content="summary_large_image" />}
      <meta name="twitter:site" content="@SuperhumanHQ" />
    </>
  )

  return (
    <Head>
      {/* Force CSS layers to be loaded in correct order - https://github.com/vercel/next.js/issues/64921 */}
      <style>{`
        @layer reset, theme, base, components, atoms;
      `}</style>
      <title>{seoTitle}</title>
      {robots.length > 0 && <meta name="robots" content={robots.join(', ')} key="robots" />}
      {seoDescription.trim() && (
        <meta name="description" content={seoDescription} key="description" />
      )}
      {canonicalUrl.trim() && <link rel="canonical" href={canonicalUrl} key="canonical" />}
      {social}
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(',')} key="keywords" />}
      {CDN_URL && (
        <>
          <link rel="preconnect" href={CDN_URL} crossOrigin="anonymous" />
          <link rel="dns-prefetch" href={CDN_URL} />
        </>
      )}
      {getFontsForPreloadLinks(fontFamilies).map(({ key, url, format }) => (
        <link
          key={key}
          rel="preload"
          as="font"
          href={url}
          type={`font/${format}`}
          crossOrigin="anonymous"
        />
      ))}
      {/* Preload selected hero person image on homepage for LCP optimization */}
      {isHomepage(slug) &&
        (heroImageSeed || data?.heroImageSeed) &&
        (() => {
          const preload = getHeroImagePreloadProps(heroImageSeed || data.heroImageSeed!)
          return preload ? <link key={preload.key} {...preload.props} /> : null
        })()}
      {fontFamilies}
      {faviconsOriginal.map(({ key, ...restProps }) => (
        <link key={key} {...restProps} />
      ))}
      <script
        key="server-data"
        dangerouslySetInnerHTML={{
          __html: `window.ShData=${serializeServerData(data)};Object.freeze(window.ShData);`,
        }}
      />
      <style dangerouslySetInnerHTML={{ __html: getStyleFontFaces(fontFamilies) }} />
      {!isPreview && (
        <script
          id="transcend-airgap"
          key="transcend-airgap"
          src={`https://transcend-cdn.com/cm/${transcendConfigId}/airgap.js`}
          data-cfasync="false"
          data-languages="en"
          data-css={withPublicCssUrl('transcend-airgap.css')}
        />
      )}
    </Head>
  )
}
